/**
 * OfferDetailModal
 * Modal con todo el detalle de una oferta (usuario, oferta, opciones de pago,
 * desglose de comision). Reemplaza la navegacion directa a Perfil Financiero
 * desde la lista de ofertas.
 */

import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';
import { CopyButton } from '@/components/ui/CopyButton';
import { CreditLevelBadge } from '@/features/credit/components/CreditLevelBadge';
import { OfferOptionsTable } from '@/features/credit/components/OfferOptionsTable';
import { FeeBreakdown } from '@/features/credit/components/FeeBreakdown';
import { useOfferDetail } from '@/features/credit/hooks';
import { formatCurrency } from '@/utils/formatters';
import { CreditLevel } from '@/types/credit.types';

interface OfferDetailModalProps {
  isOpen: boolean;
  offerId: string;
  directoryId: string;
  onClose: () => void;
}

type BadgeVariant = 'default' | 'success' | 'warning' | 'danger' | 'info';

function getStatusVariant(status: string): BadgeVariant {
  switch (status) {
    case 'CONVERTED':
    case 'ACCEPTED':
      return 'success';
    case 'VIEWING':
      return 'info';
    case 'LOCKED':
      return 'warning';
    case 'EXPIRED':
      return 'danger';
    case 'ABANDONED':
      return 'default';
    default:
      return 'default';
  }
}

function formatDateLong(dateStr?: string): string {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return new Intl.DateTimeFormat('es-MX', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

function InfoRow({
  label,
  value,
  title,
  truncate,
}: {
  label: string;
  value: React.ReactNode;
  title?: string | undefined;
  truncate?: boolean | undefined;
}) {
  return (
    <div className="flex items-center justify-between gap-3 py-1.5">
      <span className="shrink-0 text-sm text-muted-foreground">{label}</span>
      <span
        className={`text-right text-sm font-medium tabular-nums text-foreground ${
          truncate ? 'min-w-0 truncate' : ''
        }`}
        title={title}
      >
        {value ?? '-'}
      </span>
    </div>
  );
}


export function OfferDetailModal({
  isOpen,
  offerId,
  directoryId,
  onClose,
}: OfferDetailModalProps) {
  const { offer, user, isLoading, isError, refetch } = useOfferDetail(offerId, directoryId);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Detalle de Oferta"
      titleAction={offerId ? <CopyButton value={offerId} label="Offer ID" /> : undefined}
      size="lg"
    >
      <div className="space-y-4">
        {isLoading && <ModalSkeleton />}

        {isError && (
          <div className="flex flex-col items-center justify-center rounded-lg border border-border bg-card px-6 py-10">
            <AlertTriangle size={36} className="mb-3 text-muted-foreground" />
            <h3 className="text-base font-semibold text-foreground">
              Error al cargar la oferta
            </h3>
            <Button className="mt-4" onClick={() => refetch()}>
              <RefreshCw size={16} />
              Reintentar
            </Button>
          </div>
        )}

        {offer && !isLoading && !isError && (
          <>
            {/* Row 1: Usuario + Oferta */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <Card title="Usuario">
                <div className="space-y-1">
                  {user ? (
                    <>
                      <InfoRow label="Nombre" value={user.full_name && user.full_name !== 'N/A' ? user.full_name : '—'} />
                      <InfoRow label="Telefono" value={user.phone_number} />
                      <InfoRow
                        label="Directory ID"
                        value={<CopyButton value={user.directory_id} label="Directory ID" variant="plain" />}
                      />
                      <InfoRow
                        label="People ID"
                        value={<CopyButton value={user.people_id} label="People ID" variant="plain" />}
                      />
                    </>
                  ) : (
                    <p className="text-sm text-muted-foreground">Sin info de usuario</p>
                  )}
                </div>
              </Card>

              <Card title="Oferta">
                <div className="space-y-1">
                  <div className="flex items-center justify-between py-1.5">
                    <span className="text-sm text-muted-foreground">Estado</span>
                    <Badge variant={getStatusVariant(offer.status)}>{offer.status}</Badge>
                  </div>
                  <div className="flex items-center justify-between py-1.5">
                    <span className="text-sm text-muted-foreground">Nivel</span>
                    {Object.values(CreditLevel).includes(offer.credit_level as CreditLevel) ? (
                      <CreditLevelBadge level={offer.credit_level as CreditLevel} size="sm" />
                    ) : (
                      <span className="text-sm text-muted-foreground">{offer.credit_level}</span>
                    )}
                  </div>
                  <InfoRow
                    label="Monto Minimo"
                    value={formatCurrency(offer.min_amount_mxn, 'MXN', 'es-MX')}
                  />
                  <InfoRow
                    label="Monto Maximo"
                    value={formatCurrency(offer.max_amount_mxn, 'MXN', 'es-MX')}
                  />
                  {offer.available_rates && offer.available_rates.length > 0 && (
                    <div className="mt-2 border-t border-border pt-2">
                      <span className="text-xs text-muted-foreground">Plazos Disponibles</span>
                      <div className="mt-1 flex flex-wrap gap-1.5">
                        {offer.available_rates.map((r) => (
                          <span
                            key={r.term_weeks}
                            className="inline-flex items-center rounded-md border border-border bg-muted/50 px-2 py-0.5 text-xs tabular-nums"
                          >
                            {r.term_weeks} sem · {(r.annual_rate * 100).toFixed(0)}%
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="mt-2 border-t border-border pt-2">
                    <InfoRow label="Expira" value={formatDateLong(offer.offer_expires_at)} />
                    <InfoRow label="Creada" value={formatDateLong(offer.created_at)} />
                    <InfoRow label="Actualizada" value={formatDateLong(offer.updated_at)} />
                  </div>
                </div>
              </Card>
            </div>

            {/* Row 2: Seleccion del Cliente (si aplica) */}
            {offer.selected_term_weeks != null && (() => {
              const selectedOption = (offer.options ?? []).find(
                (o) => o.term_weeks === offer.selected_term_weeks
              );
              const requestedAmount =
                selectedOption?.requested_amount_mxn ?? offer.selected_amount_mxn;
              return (
                <Card title="Seleccion del Cliente" description="Plan elegido por el cliente">
                  <div className="grid grid-cols-2 gap-x-8 gap-y-1 sm:grid-cols-3">
                    <InfoRow label="Plazo elegido" value={`${offer.selected_term_weeks} semanas`} />
                    <InfoRow
                      label="Tasa aplicada"
                      value={
                        offer.selected_rate != null
                          ? `${(offer.selected_rate * 100).toFixed(1)}%`
                          : '-'
                      }
                    />
                    <InfoRow
                      label="Pago semanal"
                      value={
                        offer.weekly_payment_mxn != null
                          ? formatCurrency(offer.weekly_payment_mxn, 'MXN', 'es-MX')
                          : '-'
                      }
                    />
                    <InfoRow
                      label="Total a pagar"
                      value={
                        offer.total_to_pay_mxn != null
                          ? formatCurrency(offer.total_to_pay_mxn, 'MXN', 'es-MX')
                          : '-'
                      }
                    />
                    <InfoRow
                      label="Total intereses"
                      value={
                        offer.total_interest_mxn != null
                          ? formatCurrency(offer.total_interest_mxn, 'MXN', 'es-MX')
                          : '-'
                      }
                    />
                    {offer.accepted_at && (
                      <InfoRow label="Aceptada" value={formatDateLong(offer.accepted_at)} />
                    )}
                    {offer.abandoned_at && (
                      <InfoRow label="Abandonada" value={formatDateLong(offer.abandoned_at)} />
                    )}
                  </div>
                  <div className="mt-4 border-t border-border pt-3">
                    <p className="mb-2 text-xs font-medium text-muted-foreground">
                      Desglose del monto financiado
                    </p>
                    <FeeBreakdown
                      requestedAmountMxn={requestedAmount}
                      openingFeeMxn={selectedOption?.opening_fee_mxn}
                      openingFeeIvaMxn={selectedOption?.opening_fee_iva_mxn}
                      openingFeePct={selectedOption?.opening_fee_pct}
                      financedAmountMxn={selectedOption?.financed_amount_mxn}
                    />
                  </div>
                </Card>
              );
            })()}

            {/* Row 3: Opciones de Pago */}
            <Card title="Opciones de Pago" description="Planes disponibles para esta oferta">
              <OfferOptionsTable options={offer.options ?? []} />
            </Card>

            {/* Footer actions */}
            <div className="flex items-center justify-end gap-3 border-t border-border pt-3">
              <Button variant="secondary" onClick={onClose}>
                Cerrar
              </Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

function ModalSkeleton() {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Skeleton variant="rectangular" height={200} />
        <Skeleton variant="rectangular" height={200} />
      </div>
      <Skeleton variant="rectangular" height={300} />
    </div>
  );
}
